import React, { useEffect, useState } from "react";
import API from "../api";
import { Card, Loading, SectionHeader } from "../components/UI";
import { useAuth } from "../context/AuthContext";

export default function Analytics() {
  const [stats, setStats] = useState(null);
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState("");
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  useEffect(() => {
    if (user?.role === "admin") {
      API.get("/clients").then(r => setClients(r.data)).catch(console.error);
    }
    fetchStats();
  }, []);

  useEffect(() => {
    fetchStats();
  }, [selectedClient]);

  const fetchStats = async () => {
    setLoading(true);
    try {
      const url = selectedClient ? `/leads/stats?clientId=${selectedClient}` : "/leads/stats";
      const res = await API.get(url);
      setStats(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <Loading />;

  const maxDaily = Math.max(...(stats?.daily?.map(d => d.count) || [1]), 1);

  return (
    <div className="fade-in">
      <SectionHeader title="Analytics" subtitle="Lead performance aur conversion tracking" />

      {/* Client Filter — Admin only */}
      {user?.role === "admin" && (
        <div style={{ marginBottom: 20 }}>
          <select
            value={selectedClient}
            onChange={e => setSelectedClient(e.target.value)}
            style={s.select}
          >
            <option value="">All Clients</option>
            {clients.map(c => (
              <option key={c._id} value={c._id}>{c.name}</option>
            ))}
          </select>
        </div>
      )}

      {/* Stats Cards */}
      <div style={s.statsGrid}>
        {[
          { label: "Total Leads", value: stats?.total || 0, icon: "◎", color: "#25d366" },
          { label: "🔥 Hot Leads", value: stats?.hot || 0, icon: "🔥", color: "#ff4d4d" },
          { label: "🌡️ Warm Leads", value: stats?.warm || 0, icon: "🌡️", color: "#ff9500" },
          { label: "❄️ Cold Leads", value: stats?.cold || 0, icon: "❄️", color: "#4d9fff" },
        ].map((stat, i) => (
          <Card key={i} style={s.statCard}>
            <div style={{ fontSize: 28, fontWeight: 700, color: stat.color, fontFamily: "var(--font-mono)" }}>
              {stat.value}
            </div>
            <div style={{ fontSize: 12, color: "var(--text-secondary)", marginTop: 4 }}>{stat.label}</div>
          </Card>
        ))}
      </div>

      <div style={s.twoCol}>
        {/* Daily Chart */}
        <Card>
          <div style={s.cardTitle}>Last 7 Days</div>
          <div style={s.chartWrapper}>
            {stats?.daily?.map((d, i) => (
              <div key={i} style={s.barGroup}>
                <div style={s.barValue}>{d.count}</div>
                <div style={{
                  ...s.bar,
                  height: `${Math.max((d.count / maxDaily) * 120, 4)}px`,
                  background: d.count > 0 ? "var(--accent)" : "var(--border)",
                }} />
                <div style={s.barLabel}>{d.date}</div>
              </div>
            ))}
          </div>
        </Card>

        {/* Status Breakdown */}
        <Card>
          <div style={s.cardTitle}>CRM Status</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 16 }}>
            {[
              { label: "New", value: stats?.status?.newLeads || 0, color: "#4d9fff" },
              { label: "Contacted", value: stats?.status?.contacted || 0, color: "#ff9500" },
              { label: "Interested", value: stats?.status?.interested || 0, color: "#25d366" },
              { label: "Closed", value: stats?.status?.closed || 0, color: "#25d366" },
              { label: "Lost", value: stats?.status?.lost || 0, color: "#ff4d4d" },
            ].map((item, i) => (
              <div key={i} style={s.statusRow}>
                <div style={{ fontSize: 13, width: 90, color: "var(--text-secondary)" }}>{item.label}</div>
                <div style={s.progressBg}>
                  <div style={{
                    ...s.progressBar,
                    width: `${stats?.total ? (item.value / stats.total) * 100 : 0}%`,
                    background: item.color,
                  }} />
                </div>
                <div style={{ fontSize: 13, fontWeight: 700, width: 30, textAlign: "right", fontFamily: "var(--font-mono)" }}>
                  {item.value}
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* Interest Breakdown */}
      <Card style={{ marginTop: 16 }}>
        <div style={s.cardTitle}>Interest Breakdown</div>
        <div style={s.interestGrid}>
          {[
            { label: "Bike Inquiries", value: stats?.bikeLeads || 0, icon: "🏍️", color: "#ff9500" },
            { label: "Service Bookings", value: stats?.serviceLeads || 0, icon: "🔧", color: "#4d9fff" },
          ].map((item, i) => (
            <div key={i} style={s.interestCard}>
              <div style={{ fontSize: 32 }}>{item.icon}</div>
              <div style={{ fontSize: 28, fontWeight: 700, color: item.color, fontFamily: "var(--font-mono)" }}>
                {item.value}
              </div>
              <div style={{ fontSize: 13, color: "var(--text-secondary)" }}>{item.label}</div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

const s = {
  select: { background: "var(--bg-card)", border: "1px solid var(--border)", borderRadius: "var(--radius)", padding: "8px 12px", color: "var(--text-primary)", fontSize: 13, fontFamily: "var(--font-body)", cursor: "pointer", outline: "none" },
  statsGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 16, marginBottom: 16 },
  statCard: { textAlign: "center", padding: 20 },
  twoCol: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 },
  cardTitle: { fontSize: 13, fontWeight: 700, color: "var(--text-secondary)", textTransform: "uppercase", letterSpacing: "0.06em", fontFamily: "var(--font-mono)", marginBottom: 8 },
  chartWrapper: { display: "flex", alignItems: "flex-end", gap: 8, height: 160, marginTop: 16, padding: "0 4px" },
  barGroup: { display: "flex", flexDirection: "column", alignItems: "center", gap: 4, flex: 1 },
  barValue: { fontSize: 10, color: "var(--text-muted)", fontFamily: "var(--font-mono)" },
  bar: { width: "100%", borderRadius: "4px 4px 0 0", minHeight: 4, transition: "height 0.3s" },
  barLabel: { fontSize: 9, color: "var(--text-muted)", textAlign: "center", fontFamily: "var(--font-mono)" },
  statusRow: { display: "flex", alignItems: "center", gap: 10 },
  progressBg: { flex: 1, height: 8, background: "var(--bg-elevated)", borderRadius: 4, overflow: "hidden" },
  progressBar: { height: "100%", borderRadius: 4, transition: "width 0.3s" },
  interestGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 16, marginTop: 16 },
  interestCard: { background: "var(--bg-elevated)", borderRadius: "var(--radius)", padding: 24, textAlign: "center", display: "flex", flexDirection: "column", gap: 8, alignItems: "center" },
};
